import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import TabGridAndFormLayout from "../../../Components/ProForms/ProFormsLayout/TabGridAndFormLayout";
import ProGrid from "../../../Components/ProGrid/v2";
import EditCell from "../../../Components/ProGrid/components/EditCell";
import DeleteCell from "../../../Components/ProGrid/components/DeleteCell";
import ProTextInput from "../../../Components/Inputs/TextField";
import { STAUTES } from "../../../../utils/constant";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import usePageState from "../../../../utils/customHooks/usePageState";
import { prepareDefaultValues, prepareInitialConfig, employeeNotesFormName, changeMode } from "../../../../utils/formHelper";
import { GET_ALL_NOTES_V2, ADD_EMPLOYEE_NOTE_V2, UPDATE_EMPLOYEE_NOTE_V2, DELETE_EMPLOYEE_NOTE_V2 } from "../../../../utils/services/apiPath";
import { deleteApi, getApi, postApi, putApi } from "../../../../utils/services";
import { employeesNotesPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import usePermission from "../../../../utils/customHooks/usePermission";
import useColumn from "../../../../utils/customHooks/useColumns";
import { EmployeeNotesFormConfing } from "../EmployeeData";
import { Box, Button, Paper, Typography, Popover } from "@mui/material";
import dayjs from "dayjs";

export default function EmployeeNotes() {
  let { id } = useParams();
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(EmployeeNotesFormConfing, "edit"));
  const [employeeNotesData, setEmployeeNotesData] = useState({});
  const [actionData, setActionData] = useState({});
  const [isAddMode, setIsAddMode] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverNote, setPopoverNote] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const hoverTimeout = useRef(null);
  const {
    currentPage,
    pageSize,
    data: notesData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();
  const { "Notes List": notesListPermission } = usePermission(
    employeesNotesPermissionSelector,
    employeeNotesFormName
  );

  const alwaysAllowPermission = { canView: true, canAdd: true, canEdit: true };

  const handlePopoverOpen = (event, note) => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setAnchorEl(event.currentTarget);
    setPopoverNote(note);
    setPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setPopoverOpen(false);
    setAnchorEl(null);
    setPopoverNote("");
  };

  const handleSpanMouseLeave = () => {
    // Delay closing to allow mouse to enter popover
    hoverTimeout.current = setTimeout(() => {
      setPopoverOpen(false);
      setAnchorEl(null);
      setPopoverNote("");
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
  };

  const handlePopoverMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      setPopoverOpen(false);
      setAnchorEl(null);
      setPopoverNote("");
    }, 150);
  };

  const columns = useColumn(
    [
      {
        field: "createdDate",
        headerName: "Date & Time Added",
        flex: 1,
        renderCell: params => {
          const value = params.value;
          return value ? dayjs(value).format("MM/DD/YYYY h:mm A") : "";
        }
      },
      {
        field: "noteTitle",
        headerName: "Title",
        flex: 1
      },
      {
        field: "notes",
        headerName: "Note",
        flex: 1,
        renderCell: params => {
          const value = params.value || "";
          const displayText = value.length > 20 ? value.slice(0, 20) + "..." : value;
          return (
            <span
              style={{ cursor: "pointer" }}
              onMouseEnter={e => handlePopoverOpen(e, value)}
              onMouseLeave={handleSpanMouseLeave}
            >
              {displayText}
            </span>
          );
        }
      },
      {
        field: "createdByName",
        headerName: "Author",
        width: 150,
        flex: 1
      },
      {
        field: "employeeNoteId",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => (
          <>
            <EditCell params={params} onEdit={onEdit} />
            <DeleteCell params={params} onDelete={onDelete} />
          </>
        )
      }
    ],
    notesListPermission.canDelete
  );

  const { handleSubmit, control, setValue, reset, watch } = useForm({ defaultValues: prepareDefaultValues(EmployeeNotesFormConfing) });
  const isEditModeOn = useMemo(() => Object.values(formConfig).some(({ mode }) => mode === "edit"), [formConfig]);
  const noteValue = watch("note") || "";

  const handleReadValueClick = name => {
    setFormConfig(prev => ({ ...prev, [name]: { ...prev[name], mode: "edit" } }));
  };

  const resetForm = () => {
    setActionData({});
    setFormConfig(changeMode());
    reset(prepareDefaultValues(EmployeeNotesFormConfing));
  };
  
  const onCancel = () => {
    if (actionData?.employeeNoteId) {
      setFormConfig(changeMode("read"));
    } else {
      resetForm();
    }
  };
  
  const onSave = async data => {
    if (isSaving) return; 
    setIsSaving(true);

    const payload = {
      noteTitle: data?.noteTitle,
      notes: data?.note,
      isPrivate: false,
      employeeId: id
    };

    const { error } = await postApi(ADD_EMPLOYEE_NOTE_V2(id), payload);
    setIsSaving(false); 

    if (error) return errorToast(error);

    if (currentPage !== 0) {
      setPagination({ page: 0, pageSize: pageSize });
    } else {
      getAllNotes();
    }
    successToast("Employee Note Added Successfully");
    resetForm();
  };

  const onEdit = async (updatedNote) => {
    setIsSaving(true);

    const payload = {
      employeeNoteId: updatedNote.employeeNoteId,
      noteTitle: updatedNote.noteTitle,
      notes: updatedNote.notes,
      isPrivate: updatedNote.isPrivate ?? false,
      employeeId: id
    };

    const { error } = await putApi(UPDATE_EMPLOYEE_NOTE_V2(id, updatedNote.employeeNoteId), payload);
    setIsSaving(false);
    if (error) return errorToast(error);
    getAllNotes();
    successToast("Employee Note Updated Successfully");
  };

  const onDelete = async noteId => {
    const { error } = await deleteApi(DELETE_EMPLOYEE_NOTE_V2(id, noteId));
    if (error) return errorToast(error);
    getAllNotes();
    successToast("Note deleted successfully.");
  };

  const getAllNotes = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    
    const queryParams = new URLSearchParams({
      $count: true,
      $skip: currentPage * pageSize,
      $top: pageSize,
      $orderby: sortModel?.field 
        ? `${sortModel.field} ${sortModel.sort === 'asc' ? 'asc' : 'desc'}` 
        : 'createdDate desc'
    });

    if (searchString) {
      const sanitizedSearch = searchString.toLowerCase().replace(/'/g, "''");

      queryParams.append(
        '$filter', 
        `contains(tolower(notes), '${sanitizedSearch}') or contains(tolower(noteTitle), '${sanitizedSearch}')`
      );
    }

    const { data: notesArray, totalRecords, error } = await getApi(
      `${GET_ALL_NOTES_V2(id)}?${queryParams.toString()}`
    );

    setStatus(STAUTES.IDLE);
    
    if (error) return errorToast(error);
    
    setPageState(prevPageInfo => ({ 
      ...prevPageInfo, 
      data: notesArray, 
      rowCount: totalRecords || 0 
    }));
  }, [id, currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  useEffect(() => {
    getAllNotes();
  }, [getAllNotes]);

  const defaultFormProps = { control, formValues: employeeNotesData, isAddMode, handleReadValueClick };

  return (
    <TabGridAndFormLayout
      showGrid={true}
      title={"Notes"}
      backAction={() => resetForm()}
      backLabel="Go back to Notes List"
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      canView={notesListPermission.canView}
      hideEdit={true}
      onSave={handleSubmit(onSave)}
      onCancel={onCancel}
      isSaving={isSaving}
    >
      {/* Add Note Section */}
      <div style={{ width: "100%", paddingLeft: 20, paddingRight: 20 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ maxWidth: 1400, margin: '16px auto 8px auto' }} gutterBottom>
          Add Note
        </Typography>
        <Paper 
          elevation={2}
          style={{
            padding: 24,
            marginBottom: 24,
            borderRadius: 8,
            border: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            gap: 30,
            width: '100%',
            maxWidth: 1400,
            margin: "0 auto",
            boxSizing: 'border-box'
          }}
        >
          <ProTextInput
            name="noteTitle"
            label="Title"
            placeholder={"Please enter title here"}
            permission={alwaysAllowPermission}
            rules={{ required: "Title is required!" }}
            fullWidth
            {...defaultFormProps}
          />
          <Box>
            <ProTextInput
              name="note"
              label="Note"
              placeholder={"Please enter note here"}
              permission={alwaysAllowPermission}
              rules={{ 
                required: "Note is required!",
                maxLength: { value: 500, message: "Note cannot exceed 500 characters." }
              }}
              fullWidth
              multiline
              minRows={4}
              {...defaultFormProps}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
              {noteValue.length}/500 characters
            </Typography>
          </Box>

          {/* Button Container */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button variant="outlined" color="secondary" onClick={resetForm}>
              Reset
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit(onSave)} disabled={isSaving}>
              {isSaving ? "Adding..." : "Add"}
            </Button>
          </div>
        </Paper>
      </div>

      {/* Notes History Section */}
      <div style={{ width: "100%", marginTop: 18, boxSizing: "border-box" }}>
        <ProGrid
          title="Notes History"
          loading={status === STAUTES.LOADING}
          options={{
            getRowId: row => row.employeeNoteId,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
          columns={columns}
          rows={notesData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
          hideExport={!notesListPermission.canExport}
          hideAddButton={true}
        />
      </div>

      {/* Popover for full note on hover */}
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            sx: { 
              maxWidth: 400, 
              padding: 2, 
              whiteSpace: 'pre-wrap',
              pointerEvents: 'auto'
            },
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave
          }
        }}
      >
        <Typography variant="body2">{popoverNote}</Typography>
      </Popover>
    </TabGridAndFormLayout>
  );
}
